import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Link,
} from "react-router-dom";
import { lazy, Suspense, useCallback, useEffect } from "react";
import { Sentry } from "@/lib/monitoring/sentry";
import { trackPageView, identifyUser, resetUser } from "@/lib/monitoring/posthog";

// Eagerly loaded — public/small pages that must be available instantly
import { LandingPage } from "@/app/routes/LandingPage";
import { ForgotPasswordPage } from "@/app/routes/ForgotPasswordPage";
import { ResetPasswordPage } from "@/app/routes/ResetPasswordPage";
import { LoginPage } from "@/app/routes/LoginPage";
import { SignupPage } from "@/app/routes/SignupPage";
import { BillingPage } from "@/app/routes/BillingPage";
import { NotFoundPage } from "@/app/routes/NotFoundPage";

// Lazy loaded — auth-gated or rarely visited pages
const DashboardPage = lazy(() => import("@/app/routes/DashboardPage").then(m => ({ default: m.DashboardPage })));
const OnboardingPage = lazy(() => import("@/app/routes/OnboardingPage").then(m => ({ default: m.OnboardingPage })));
const WorkoutDetailPage = lazy(() => import("@/app/routes/WorkoutDetailPage").then(m => ({ default: m.WorkoutDetailPage })));
const HistoryPage = lazy(() => import("@/app/routes/HistoryPage").then(m => ({ default: m.HistoryPage })));
const ZonesPage = lazy(() => import("@/app/routes/ZonesPage").then(m => ({ default: m.ZonesPage })));
const AnalyticsPage = lazy(() => import("@/app/routes/AnalyticsPage").then(m => ({ default: m.AnalyticsPage })));
const DevWorkoutPreviewPage = lazy(() => import("@/app/routes/DevWorkoutPreviewPage").then(m => ({ default: m.DevWorkoutPreviewPage })));
const CoachPage = lazy(() => import("@/app/routes/CoachPage").then(m => ({ default: m.CoachPage })));
const OAuthCallback = lazy(() => import("@/app/routes/OAuthCallback").then(m => ({ default: m.OAuthCallback })));
const PrivacyPolicyPage = lazy(() => import("@/app/routes/PrivacyPolicyPage").then(m => ({ default: m.PrivacyPolicyPage })));
const TermsPage = lazy(() => import("@/app/routes/TermsPage").then(m => ({ default: m.TermsPage })));

import {
  ProtectedRoute,
  GuestOnlyRoute,
  SubscriptionRequired,
  CoachRoute,
} from "@/app/routes/ProtectedRoute";
import { useThemeStore } from "@/state/themeStore";
import { useAuthStore } from "@/state/authStore";
import { useDataSync } from "@/hooks/useDataSync";
import { flushPendingProfileSave } from "@/state/profileStore";
import { Toaster } from "@/components/Toast/Toaster";

import { CookieConsent } from "@/components/CookieConsent";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";

function PageLoader() {
  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-zinc-700 border-t-[rgb(var(--cp,79,70,229))] rounded-full animate-spin" />
    </div>
  );
}

const NO_FOOTER_ROUTES = [
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/privacy-policy",
  "/terms",
];

function AppFooter() {
  const { pathname } = useLocation();
  const account = useAuthStore((s) => s.account);
  if (NO_FOOTER_ROUTES.includes(pathname)) return null;
  return (
    <footer className={`w-full px-4 pt-4 text-center ${account ? "pb-24 lg:pb-4" : "pb-4"}`}>
      <p className="mx-auto max-w-4xl text-xs leading-relaxed text-zinc-400 tracking-wide">
        © 2026 Momentum Peaks · Powered by{" "}
        <span className="text-zinc-400 font-medium">blueClone</span> · Created
        by <span className="text-zinc-400 font-medium">Cabezoski</span> ·{" "}
        <Link
          to="/privacy-policy"
          className="text-zinc-400 hover:text-zinc-200 underline transition-colors"
        >
          Privacy Policy
        </Link>{" "}
        ·{" "}
        <Link
          to="/terms"
          className="text-zinc-400 hover:text-zinc-200 underline transition-colors"
        >
          Terms
        </Link>
      </p>
    </footer>
  );
}

/** Fires a PostHog $pageview on every React Router navigation. */
function PageViewTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PageTransition({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return (
    <div key={pathname} className="page-enter">
      {children}
    </div>
  );
}

function App() {
  const { getActiveTheme, themeId } = useThemeStore();
  const { initializeAuth } = useAuthStore();
  const account = useAuthStore((s) => s.account);
  useDataSync();

  // Flush any pending debounced profile saves when the user closes/navigates away.
  const handleBeforeUnload = useCallback(() => {
    flushPendingProfileSave();
  }, []);
  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [handleBeforeUnload]);

  // Bootstrap Supabase auth on mount.
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    void initializeAuth().then((unsubscribe) => {
      cleanup = unsubscribe;
    });
    return () => cleanup?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Identify user in Sentry + PostHog whenever auth state resolves.
  useEffect(() => {
    if (account) {
      identifyUser(account.id, { email: account.email });
    } else {
      resetUser();
    }
  }, [account]);

  // Apply saved theme CSS vars on first render and when accent changes
  useEffect(() => {
    const theme = getActiveTheme();
    document.documentElement.style.setProperty("--cp", theme.primaryRgb);
    document.documentElement.style.setProperty("--cs", theme.secondaryRgb);
    document.documentElement.setAttribute("data-mode", "light");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeId]);

  return (
    <Sentry.ErrorBoundary fallback={<AppErrorFallback />} showDialog={false}>
      <BrowserRouter>
        <div className="flex flex-col min-h-screen">
          {/* Skip navigation — first focusable element on every page */}
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[10000] focus:px-4 focus:py-2 focus:bg-indigo-600 focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none"
          >
            Skip to main content
          </a>
          <PageViewTracker />
          <ScrollToTop />
          <div className={`min-w-0 flex-1${account ? " pb-20 lg:pb-0" : ""}`}>
            <Suspense fallback={<PageLoader />}>
              <PageTransition>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route element={<CoachRoute />}>
                  <Route path="/dev/workouts" element={<DevWorkoutPreviewPage />} />
                  <Route path="/dev/coach" element={<CoachPage />} />
                </Route>
                <Route path="/oauth/wahoo/callback" element={<OAuthCallback />} />

                {/* Public auth utility routes — must NOT be behind GuestOnlyRoute
                  because /reset-password is visited while holding a temporary
                  Supabase recovery session (user is technically "authenticated") */}
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* Guest Only (redirect if authenticated) */}
                <Route element={<GuestOnlyRoute />}>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                </Route>

                {/* Protected Routes (require authentication) */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/onboarding" element={<OnboardingPage />} />

                  {/* Subscription Required */}
                  <Route element={<SubscriptionRequired />}>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route
                      path="/workout/:workoutId"
                      element={<WorkoutDetailPage />}
                    />
                    <Route path="/history" element={<HistoryPage />} />
                    <Route path="/zones" element={<ZonesPage />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                  </Route>
                </Route>

                {/* Catch all - 404 page */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              </PageTransition>
            </Suspense>
          </div>

          {/* Global mobile bottom nav — visible on all authenticated routes */}
          {account && <MobileBottomNav />}

          {/* Footer */}
          <AppFooter />

          {/* Toast notifications — rendered outside the route tree so they
            persist across page navigations */}
          <Toaster />

          {/* Cookie / analytics consent banner */}
          <CookieConsent />
        </div>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}

function AppErrorFallback() {
  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-6">
      <div className="text-center max-w-sm">
        <h2 className="text-2xl font-bold text-white mb-3">Something went wrong</h2>
        <p className="text-zinc-400 text-sm mb-6">
          An unexpected error occurred. Our team has been notified automatically.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-[rgb(var(--cp,79,70,229))] hover:opacity-90 text-white font-semibold rounded-lg transition-opacity"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

export default App;
